import { useState } from 'react';
import { Row, Col, Card, Collapse, Button, Divider, DatePicker, Progress, Radio, Typography, Space } from 'antd';
import { red, green, blue } from '@ant-design/colors';
import { Document, PDFDownloadLink } from '@react-pdf/renderer';
import { map as _map, reduce as _reduce } from 'lodash';
import moment from 'moment';
import {
  convertCentsIntoDollar,
  formatDate,
  isSupportAgent2,
  isSupportAgent3
} from '../../../../helpers/genericHelper';
import { MyDocument } from '../PdfFile';
import SBPaymentHistoryComponent from '../SBPaymentHistory';
import Data from '../../../CustomText/data';
import StatementList from './StatementsList';
import PaymentSchedule from './PaymentSchedule';
import PaymentAttemptsComponent from '../PaymentAttempts';
import PaymentAttemptsList from './PaymentAttemptsList';
import { PersistedCollapse } from '../../../PersistedSections';
import usePageState from '../../../../helpers/usePageState';

const { Panel } = Collapse;
const { RangePicker } = DatePicker;
const { Title } = Typography;

export default function LoanDetails({
  data,
  loanData,
  userSummary,
  showRefundModalVisible,
  showCloseAccountModal,
  openToggleAutoRenewModal,
  showChargeSilaModal,
  paymentAttempts,
  autoPayStatusEnabled,
  autoPayStatusData
}) {
    
    const [startDate, setStartDate] = useState(moment().subtract(6, 'months'));
    const [endDate, setEndDate] = useState(moment());
    const [paymentType, setPaymentType] = usePageState(`loan-payment-type:${data?.sbLoanEntry?.id}`, 'payments');

    if (!loanData || !data) {
      return <>Loan details are not available</>;
    }

    const { loanDetails, loanStatements } = loanData[data.sbLoanEntry?.id] || false;
    if (!data || !data.sbLoanEntry) { return <>Cannot Load Loan</>; }

    
    const handleDateChange = (dates) => {
      setStartDate(dates[0]);
      setEndDate(dates[1])
    }

    const sbPayments = _reduce(data?.allPayments, (accumulator, payment) => {
      accumulator.push({
          //...payment,
          debit: true,
          amountInCents: payment?.amount || 0,
          transactionTime: payment?.paymentDueDate || 0,
          descriptor: 'Payment Due'
      });

      if (payment?.paymentSuccessTs) {
          accumulator.push({
          //...payment,
          debit: false,
          amountInCents: payment?.amount || 0,
          transactionTime: payment?.paymentSuccessTs || 0,
          descriptor: 'Payment Received'
          });
      }
      return accumulator;
    }, []);

    if (data?.sbLoanEntry?.loanStatus === 'CANCELLED') {
      sbPayments.push({
          debit: false,
          amountInCents: data?.remainingDeposits || 0,
          transactionTime: data?.closureDate || 0,
          descriptor: 'Payment Received'
      });
    }

    console.log('sbPayments', sbPayments);

    const totalCost = data.loanMetadata?.costInCents || 0;
    const monthlyCost = totalCost / 12;
    const principalMonthly = data.monthlyPlanAmount - monthlyCost;
    console.log('sbPayments', totalCost, monthlyCost, principalMonthly);
    
    const paymentAmount = loanStatements.toSorted(
      (a,b) => new Date(b.statementData.statementDate) - new Date(a.statementData.statementDate)
    )?.[0]?.statementData?.currentBalance
      || data?.monthlyPlanAmount || 0;
    const autopayMinBalance = Math.max(10000, Math.round(paymentAmount * 1.5));
    const manualPaymentMinBalance = Math.max(5000, Math.round(paymentAmount * 1.5));
    
    const pillColor = (value) => {
      switch (value) {
        case 'IN_PROGRESS':
        case 'ACTIVE':
          return 'processing';
        case 'PENDING':
        case 'DELINQUENT':
          return 'warning';
        case 'COMPLETED':
          return 'success';
        case 'CHARGED_OFF':
        case 'CANCELLED':
        case 'CLOSED':
          return 'error';
        default:
          return 'default';
      }
    }

    const getStatusColor = (paymentStatus, dueDate, successTime) => {
      switch (paymentStatus) {
        case 'SUCCESS':
          if (dueDate && successTime && moment(successTime).isAfter(moment(`${dueDate}T23:59:59Z`))) {
            return red[2]; // overdue
          }
          return green[6];
        case 'IN_PROGRESS':
          return blue[6]; // yellow
        default:
          if (dueDate && (!successTime || (successTime && moment(successTime).isAfter(moment(`${dueDate}T23:59:59Z`))))) {
            return red[6]; // overdue
          }
          return blue[6]; // default gray
      }
    }

    const uniquePayments = (payments) => {
      const unique = {};
      return payments.toSorted((a, b) => a.paymentNumber - b.paymentNumber).filter(payment => {
        if (!unique[payment.paymentNumber]) {
          unique[payment.paymentNumber] = true;
          return true;
        }
        return false;
      });
    }

    const paymentStrokeColors = _map(uniquePayments(data.allPayments), payment => getStatusColor(payment.paymentStatus, payment.paymentDueDate, payment.paymentSuccessTs));
    const loanTerm = loanDetails?.type?.term || 0;
    const remainingMonths = data?.remainingMonths || 0;
    const loanCompletionPercent = Math.round((paymentStrokeColors?.length || 0) / (loanTerm || 1) * 100);

    console.log('paymentStrokeColors', paymentStrokeColors);
    return (
    <Card
        bordered={false}
        hoverable={true}
        className="criclebox tablespace mb-16 bottom-bordered-card"
        key={data.id}
    >
        <Row>
          <Col span="15">
            <Data isTag tagColor={pillColor(loanDetails.loanStatus)} title="Status" value={loanDetails.loanStatus} />
            <Data isTag tagColor="success" title="Created Date" value={loanDetails.createTs ? formatDate(loanDetails.createTs, true) : null} />
            <Data isTag tagColor={loanDetails.loanOrigination ? "success" : "processing"} title="Origination Date" value={loanDetails.loanOrigination ? formatDate(loanDetails.loanOrigination, true) : null} />
            <Data isTag tagColor={loanDetails.loanClosureTs ? "error" : "processing"} title="Closure Date" value={loanDetails.loanClosureTs ? formatDate(loanDetails.loanClosureTs, true) : null} />
            <Data isTag tagColor={loanDetails.loanChargeoffTs ? "error" : "processing"} title="Chargeoff Date" value={loanDetails.loanChargeoffTs ? formatDate(loanDetails.loanChargeoffTs, true) : null} />
            <br />
            <Data isTag tagColor="processing" title="With Origination Fee" value={data.hasOriginationFee ? "YES" : "NO"} />
            <Data isTag tagColor="processing" title="Statements Enabled" value={loanDetails.billingStatementDay ? "YES" : "NO"} />
            <Data isTag tagColor={autoPayStatusEnabled ? "success" : "error"} title="Autopay Enabled" value={autoPayStatusEnabled ? "YES" : "NO"} />
            <Data isTag tagColor={autoPayStatusData.isDisabled ? "error" : "success"} title="Autopay Date" value={autoPayStatusData.autoPayDay} />
          </Col>
          <Col span="9">
            <span >
              <RangePicker
                defaultValue={[startDate, endDate]}
                onChange={handleDateChange} />
            </span>
            {userSummary && <PDFDownloadLink
                document={
                <Document>
                    <MyDocument
                    title="Ava Save & Build Credit Statement"
                    summaryData={userSummary}
                    startDate={startDate}
                    endDate={endDate}
                    last4={data.lastFour}
                    balanceDue={convertCentsIntoDollar((data?.sbLoanEntry?.loanStatus === 'CANCELLED' ? 0 : data?.remainingDeposits) || 0)}
                    list={sbPayments || []} 
                    financialProduct={loanDetails.fundsPlatform === "GALILEO"}
                    subtext={data.hasOriginationFee ? `Principal: ${convertCentsIntoDollar(principalMonthly)}, Fees: ${convertCentsIntoDollar(monthlyCost)}` : false}/>
                </Document>
                }
                fileName={`Ava Statement, ${moment(startDate).format('MMM-DD-YYYY')} - ${moment(endDate).format('MMM-DD-YYYY')}`}
            >
                {({ documentLoading }) => <span >
                  {documentLoading ? (
                      <Button>Downloading Statement...</Button>
                  ) : (
                      <Button >Download Statement</Button>
                  )}</span>
                }
            </PDFDownloadLink>}
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span="12">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Row>
                <Col span="6">
                  <Data title="Total plan amount" >
                    <Title>
                      {convertCentsIntoDollar(loanDetails?.type?.amount || 0)} 
                    </Title>
                  </Data>
                </Col>
                
                <Col span="6">
                  <Data title="Monthly Installment" >
                    <Title>
                      {convertCentsIntoDollar(loanDetails?.type?.monthlyPayment || 0)} 
                    </Title>
                  </Data>
                </Col>

                <Col span="6">
                  <Data title="Loan Cost" >
                    <Title>
                      {convertCentsIntoDollar(loanDetails?.type?.cost || 0)}
                    </Title>
                    <small>{loanDetails?.type?.cost ? `(${convertCentsIntoDollar(loanDetails?.type?.cost / loanDetails?.type?.term)} per month)` : ''}</small>
                  </Data>
                </Col>
                <Col span="6">
                  <Data title="Remaining deposits to complete plan" >
                    <Title>
                      {convertCentsIntoDollar((loanDetails?.type?.amount || 0) - (data?.savingBuilderWalletTotal || 0))} 
                    </Title>
                  </Data>
                </Col>
              </Row>
              <Row>
                <Col span="6">
                  <Data title="Total $ in S&B Credit Wallet(DB)" >
                    <Title>
                      {convertCentsIntoDollar(data?.successfulPaymentsTotal || 0)} 
                    </Title>
                  </Data>
                </Col>

                <Col span="6">
                    <Data title={`Total $ in S&B Credit Wallet (${data?.sbLoanEntry?.fundsPlatform || "SILA"})`} >
                      <Title>
                        {convertCentsIntoDollar(data?.savingBuilderWalletTotal || 0)} 
                      </Title>
                    </Data>
                </Col>

                <Col span="6">
                  <Data title="Total refunded" >
                    <Title>
                      {convertCentsIntoDollar(data?.totalRefunded || 0)} 
                    </Title>
                  </Data>
                </Col>
                
                <Col span="6">
                  <Data title="Min Balance for Autopay" >
                    <Title>
                      {convertCentsIntoDollar(autopayMinBalance)}
                    </Title>
                    <small>Max of 150% balance due or $100</small>
                  </Data>
                </Col>
                
                <Col span="6">
                  <Data title="Min Balance for Manual Payment" >
                    <Title>
                      {convertCentsIntoDollar(manualPaymentMinBalance)}
                    </Title>
                    <small>Max of 150% balance due or $50</small>
                  </Data>
                </Col>
              
              </Row>
            </Space>
          </Col>
          <Col span="1"></Col>
          <Col span="11">
            <Row>
              <Col span="4">
                <Data title="Due date" >
                  <Title>
                    {loanDetails?.paymentDueDay}
                  </Title>
                  {loanDetails?.dueDaySetByUser && <Data isTag tagColor="processing" value="Due Date Set By User" />}
                </Data>
              </Col>

              <Col span="4">
                <Data title="Billing Statement Day" >
                  <Title>
                    {loanDetails?.billingStatementDay}
                  </Title>
                </Data>
              </Col>
              <Col span="6"></Col>
              <Col span="10">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space direction="horizontal" style={{ width: '100%' }}>
                    <Data title="Loan Term">{loanTerm}</Data>
                    <Data title="Remaining Installments">{remainingMonths}</Data>
                  </Space>
                  <div style={{ marginTop: 10 }}>
                    <Progress
                      style={{ width: '100%' }}
                      steps={loanTerm}
                      percent={loanCompletionPercent}
                      strokeColor={paymentStrokeColors}
                      />
                  </div>
                </Space>
              </Col>
            </Row>
            <Row>
              <Col span="4">
                <Data title="Auto Pay Day" >
                  <Title>
                    {loanDetails?.autoPayDay || 'Not Set'}
                  </Title>
                </Data>
              </Col>
              <Col span="4">
                <Data title="Auto renew enabled" >
                  <Title level={3}>
                    {loanDetails?.autoRenews ? "YES" : "NO"} 
                  </Title>
                </Data>
              </Col>
              <Col span="6"></Col>
              <Col span="10">
                <Data title="Outstanding payment status" >
                  <Title level={3}>
                    {data?.outstandingPaymentStatus} 
                  </Title>
                </Data>
              </Col>
            </Row>
          </Col>
        </Row>
        
        <Divider />
        <Row className={'card-bottom-buttons-container'}>

        {isSupportAgent3() && !data?.closureDate &&
            <Button
                type={'primary'}
                className={'card-bottom-buttons'}
                onClick={() => showChargeSilaModal(data)}>
                {'Retry Payment'}
            </Button>
        }

        {isSupportAgent2() &&
            <Button
                className={'card-bottom-buttons'}
                type={'primary'}
                onClick={() => showRefundModalVisible(data)}>
                {'Refund'}
            </Button>
        }

        {isSupportAgent2() && !data?.closureDate &&
            <Button
                className={'card-bottom-buttons'}
                type={'primary'}
                onClick={() => openToggleAutoRenewModal(data)}>
                {'Toggle Auto Renew'}
            </Button>}

        {isSupportAgent2() && !data.closureDate &&
            <Button
                type={'danger'}
                onClick={() => showCloseAccountModal(data)}
                className={'card-bottom-buttons'}>
                {'Close Account'}
            </Button>
        }

        </Row>

        <Row>
          <Col span="24">
            <PersistedCollapse name={`loan-sections:${data.sbLoanEntry.id}`} ghost>
              <Panel header="Payment Schedule" key="1">
                  <PaymentSchedule
                    paymentSchedule={loanDetails.amortizations} 
                  />
              </Panel>
              <Panel header="Statements" key="2">
                <StatementList
                  statements={loanStatements}
                />
              </Panel>
              <Panel header="Payment History" key="3">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio.Group
                    onChange={(e) => setPaymentType(e.target.value)}
                    value={paymentType}
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio value="payments">Payments</Radio>
                    <Radio value="attempts">Attempts</Radio>
                  </Radio.Group>

                    {
                      paymentType === 'attempts'
                        ? <PaymentAttemptsList paymentAttempts={loanDetails.paymentAttempts} />
                        : <SBPaymentHistoryComponent
                      payments={data.allPayments}
                    />}
                  </Space>
              </Panel>
              <Panel header="Failed Payment Attempts" key="4">
                  <PaymentAttemptsComponent
                    userId={userSummary.userId}
                    accountId={loanDetails.id}
                    paymentAttempts={paymentAttempts}
                  />
              </Panel>

            </PersistedCollapse>
          </Col>
        </Row>


    </Card>
    );
}